import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, Users, List, Star, AlertCircle, X, Plus, Info, ArrowUp, ArrowDown, GripVertical, RefreshCw, AlertTriangle } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { supabase } from '../../lib/supabase';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import toast from 'react-hot-toast';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';
import { sendDraftTurnEmail } from '../../lib/api/emailNotifications';

interface DraftTeam {
  id: number;
  name: string;
  isCurrentPick: boolean;
  isMyTeam?: boolean;
  timeOfPick?: string | null;
  originalOwnerName?: string;
  pickIndex?: number;
}

interface PriorityListPlayer {
  id: number;
  name: string;
  position: string;
  teamabbrevs?: string;
  goals?: number;
  assists?: number;
  points?: number;
  salary?: number;
  isGoalie?: boolean;
  savepct?: number;
  wins?: number;
  isnhl?: boolean;
  reason_notpick?: string | null;
  isDrafted?: boolean;
}

const recentPicks = [
  { id: 1, team: "N/A", player: "Aucun choix fait pour le moment", pick: "N/A" }, 
];

// Fonction pour obtenir l'heure actuelle au fuseau horaire de Montréal
  const getMontrealTime = (): string => {
    const now = new Date();
    return now.toLocaleString('en-CA', { 
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6');
  };export function DraftPage() {
  // Tracker cette page
  usePageTracking('draftpage');

  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();

  const navigate = useNavigate();
  const location = useLocation();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUser = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const [pickTimeInMinutes, setPickTimeInMinutes] = useState(2); // Default 2 minutes
  const [pickTimeInSeconds, setPickTimeInSeconds] = useState(120); // Default 2 minutes * 60 seconds
  const [timeLeft, setTimeLeft] = useState(120);
  const [draftOrder, setDraftOrder] = useState<DraftTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPickIndex, setCurrentPickIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  const isMovingToNextPick = useRef(false);
  const [recentDraftPicks, setRecentDraftPicks] = useState<Array<{
    id: number;
    team: string;
    player: string;
    pick: string;
    timeOfPick?: string;
  }>>(recentPicks);
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
  const [serverTimeDiff, setServerTimeDiff] = useState<number>(0);
  const [isDraftModeEnabled, setIsDraftModeEnabled] = useState<boolean | null>(null);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [isResequencing, setIsResequencing] = useState(false);
  const [priorityList, setPriorityList] = useState<PriorityListPlayer[]>([]);
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [draggedPlayer, setDraggedPlayer] = useState<number | null>(null);
  const dragOriginalOrderRef = useRef<number[] | null>(null);
  // Enregistrement automatique: plus besoin d'état de réorganisation
  const [lastPriorityListRefresh, setLastPriorityListRefresh] = useState<number>(Date.now());
  const [currentPick, setCurrentPick] = useState(0);
  const [totalDraftPicks, setTotalDraftPicks] = useState(0);
  const [prevPickTime, setPrevPickTime] = useState<Date | null>(null);
  const [nextPicksPreview, setNextPicksPreview] = useState<Array<{user_id: number, pick_index: number}>>([]);
  const [isAutoPick, setIsAutoPick] = useState(false);
  const [isDraftCompleted, setIsDraftCompleted] = useState(false);
  const [showAutoPickConfirmModal, setShowAutoPickConfirmModal] = useState(false);
  const autoPickModalRef = useRef<HTMLDivElement | null>(null);
  const [showDraftOrderModal, setShowDraftOrderModal] = useState(false);
  const [allRoundsData, setAllRoundsData] = useState<Array<{round: number, picks: Array<{userId: number, name: string, originalOwnerName?: string, pickIndex: number}>}>>([]);
  const [loadingAllRounds, setLoadingAllRounds] = useState(false);

  useEffect(() => {
    if (!showAutoPickConfirmModal) return;
    const raf = window.requestAnimationFrame(() => {
      autoPickModalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [showAutoPickConfirmModal]);

  // Function to check if draft is completed and disable draft mode
  const checkAndFinishDraft = useCallback(async () => {
    if (!selectedLeague) return false;

    try {
      // Get the maximum pick_index from draft_picks table
      const { data: maxPickData, error: maxPickError } = await supabase
        .from('draft_picks')
        .select('pick_index')
        .eq('league_id', selectedLeague.id)
        .order('pick_index', { ascending: false })
        .limit(1)
        .single();

      if (maxPickError) {
        console.error('Error getting max pick index:', maxPickError);
        return false;
      }

      // Get current_pick from league_settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('league_settings')
        .select('current_pick')
        .eq('league_id', selectedLeague.id)
        .single();

      if (settingsError) {
        console.error('Error getting current pick:', settingsError);
        return false;
      }

      const maxPickIndex = maxPickData?.pick_index;
      const currentPickIndex = settingsData?.current_pick || 0;

      // If current_pick has reached or surpassed the last pick_index, finish the draft
      if (maxPickIndex !== null && currentPickIndex >= maxPickIndex) {
        // Disable draft mode
        const { error: disableDraftError } = await supabase
          .from('league_settings')
          .update({ draftMode: false })
          .eq('league_id', selectedLeague.id);

        if (disableDraftError) {
          console.error('Error disabling draft mode:', disableDraftError);
          return false;
        }

        // Clear all myturn flags
        const { error: clearTurnsError } = await supabase
          .from('draft_order')
          .update({ myturn: false })
          .eq('league_id', selectedLeague.id);

        if (clearTurnsError) {
          console.error('Error clearing myturn flags:', clearTurnsError);
        }

        // Update local state
        setIsDraftModeEnabled(false);
        setIsDraftCompleted(true);
        setDraftOrder(prev => prev.map(t => ({ ...t, isCurrentPick: false })));

        toast.success('Le repêchage est terminé ! Tous les choix ont été effectués.');
        
        return true; // Draft was completed
      }
    } catch (error) {
      console.error('Error checking draft completion:', error);
    }

    return false; // Draft not completed
  }, [selectedLeague, setIsDraftModeEnabled, setIsDraftCompleted, setDraftOrder]);

  // Check if draft mode is enabled
  useEffect(() => {
    if (!selectedLeague || !currentUser) {
      return;
    }

    async function checkDraftMode() {
      try {
        const { data, error } = await supabase
          .from('league_settings')
          .select('draftMode, isLineaire, isSerpentin, current_pick')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error) {
          console.error('Error checking draft mode:', error);
          // Default to false if error
          setIsDraftModeEnabled(false);
          setLoading(false);
          return;
        }

        setIsDraftModeEnabled(!!data?.draftMode);
        
        // Set current pick from settings
        if (data?.current_pick !== undefined) {
          setCurrentPick(data.current_pick);
        }

        setLoading(false);

        // If draft mode is not enabled, show message
        if (!data?.draftMode) {
          toast.error(language === 'fr' 
            ? 'Le mode repêchage n\'est pas activé pour cette ligue'
            : 'Draft mode is not enabled for this league');
        }
      } catch (error) {
        console.error('Error in checkDraftMode:', error);
        setIsDraftModeEnabled(false);
        setLoading(false);
      }
    }

    checkDraftMode();
  }, [selectedLeague, currentUser, navigate, language]);

  // Check if draft is already completed on initial load
  useEffect(() => {
    if (!selectedLeague || !isDraftModeEnabled) return;

    async function checkInitialDraftCompletion() {
      await checkAndFinishDraft();
    }

    checkInitialDraftCompletion();
  }, [selectedLeague, isDraftModeEnabled, checkAndFinishDraft]);

  // Periodic check for draft completion (every 30 seconds)
  useEffect(() => {
    if (!selectedLeague || !isDraftModeEnabled) return;

    const interval = setInterval(async () => {
      await checkAndFinishDraft();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [selectedLeague, isDraftModeEnabled, checkAndFinishDraft]);

  // Load league settings to get draft time per pick
  useEffect(() => {
    if (!selectedLeague) return;

    async function loadLeagueSettings() {
      try {
        const { data, error } = await supabase
          .from('league_settings')
          .select('draftMinPerPick')
          .eq('league_id', selectedLeague.id)
          .single();

        if (error) {
          console.error("Error loading league settings:", error);
          return;
        }

        if (data && data.draftMinPerPick) {
          // Get minutes from settings
          const minutes = data.draftMinPerPick;
          setPickTimeInMinutes(minutes);
          
          // Convert to seconds for timer
          const seconds = minutes * 60;
          setPickTimeInSeconds(seconds);
        }
      } catch (error) {
        console.error("Error in loadLeagueSettings:", error);
      }
    }

    loadLeagueSettings();
  }, [selectedLeague]);

  // Detect commissioner rights (main or second commissioner)
  useEffect(() => {
    if (!selectedLeague || !currentUser) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('leagues')
          .select('admin_user_id, admin_user_id2')
          .eq('league_id', selectedLeague.id)
          .single();
        if (error) {
          console.warn('Error fetching league admins', error);
          setIsCommissioner(false);
          return;
        }
        const isMain = data?.admin_user_id === Number(currentUser);
        const isSecond = data?.admin_user_id2 === Number(currentUser);
        setIsCommissioner(Boolean(isMain || isSecond));
      } catch (e) {
        console.warn('Commissioner check failed', e);
        setIsCommissioner(false);
      }
    })();
  }, [selectedLeague, currentUser]);

  // Manual resequence action (commissioner only)
  const handleResequenceNow = async () => {
    if (!selectedLeague) return;
    try {
  setIsResequencing(true);
  // Appel à resequence_draft_picks désactivé
  toast('Réindexation désactivée');
    } catch (e) {
  console.warn('Resequence action disabled', e);
    } finally {
      setIsResequencing(false);
    }
  };

  // Load draft picks
  useEffect(() => {
    if (!selectedLeague) return;

    async function loadDraftPicks() {
      try {
        // Fetch recent draft picks
        const { data, error } = await supabase
          .from('drafted_players')
          .select('dp_id, player_name, pick_number, picked_at, user_id')
          .eq('league_id', selectedLeague.id)
          .eq('is_past_draft', false)
          .order('picked_at', { ascending: false })
          // Removed limit to show all previous picks
          // .limit(10);

        if (error) {
          console.error("Error loading draft picks:", error);
          return;
        }

        if (data && data.length > 0) {
          // Get usernames for the user_ids
          const userIds = data.map(pick => pick.user_id);
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('user_id, username')
            .in('user_id', userIds);

          if (usersError) {
            console.error("Error fetching usernames:", usersError);
            return;
          }

          // Map picks with usernames
          const mappedPicks = data.map(pick => {
            const user = usersData?.find(u => u.user_id === pick.user_id);
            return {
              id: pick.dp_id, // use dp_id returned by select
              team: user?.username || `User ${pick.user_id}`,
              player: pick.player_name,
              pick: `${pick.pick_number}${pick.pick_number === 1 ? 'er' : 'e'} choix`,
              timeOfPick: pick.picked_at
            };
          });

          setRecentDraftPicks(mappedPicks);
        }
      } catch (error) {
        console.error("Error in loadDraftPicks:", error);
      }
    }

    // Initial load
    loadDraftPicks();
    
    // Set up an interval to refresh draft picks periodically
    const interval = setInterval(() => {
      loadDraftPicks();
    }, 10000); // Check for new draft picks every 10 seconds
     
    // const interval = setInterval(() => {
    //   loadDraftPicks();
    // }, 110000); // Check for new draft picks every 1m50s
    
    return () => clearInterval(interval);
  }, [selectedLeague]);

  useEffect(() => {
    if (!selectedLeague || !currentUser || !isDraftModeEnabled) {
      return;
    }
    
    async function fetchDraftOrder() {
      setLoading(true);
      try {
        // 1. Charger league_settings pour current_pick
        const { data: settingsData, error: settingsError } = await supabase
          .from('league_settings')
          .select('current_pick')
          .eq('league_id', selectedLeague.id)
          .single();

        if (settingsError) {
          console.error('Error fetching league settings:', settingsError);
          return;
        }

        const currentPickIdx = settingsData?.current_pick || 0;
        setCurrentPick(currentPickIdx);

        // 2. Charger draft_picks pour déterminer l'ordre complet
        const { data: draftPicksData, error: draftPicksError } = await supabase
          .from('draft_picks')
          .select('pick_index, round_number, position_in_round, user_id')
          .eq('league_id', selectedLeague.id)
          .order('pick_index', { ascending: true });

        if (draftPicksError) {
          console.error('Error fetching draft_picks:', draftPicksError);
          return;
        }

        if (!draftPicksData || draftPicksData.length === 0) {
          console.warn('No draft picks found');
          return;
        }

        setTotalDraftPicks(draftPicksData.length);

        // Prévisualiser les 15 prochains picks
        const upcomingPicks = draftPicksData.slice(currentPickIdx, currentPickIdx + 15);
        setNextPicksPreview(upcomingPicks);

        // 3. Trouver qui choisit maintenant via draft_picks
        const currentPickRow = draftPicksData.find(p => p.pick_index === currentPickIdx);
        if (!currentPickRow) {
          console.warn('Current pick not found in draft_picks');
          return;
        }

        const currentPickerUserId = currentPickRow.user_id;

        // 4. Charger draft_order pour les positions de base et time_of_pick
        const { data: draftOrderData, error: draftOrderError } = await supabase
          .from('draft_order')
          .select('user_id, draft_position, time_of_pick')
          .eq('league_id', selectedLeague.id)
          .order('draft_position', { ascending: true });

        if (draftOrderError) {
          console.error('Error fetching draft order:', draftOrderError);
          return;
        }

        // 5. Déterminer la ronde courante et récupérer ses picks
        const currentRoundNumber = (currentPickRow as any).round_number ?? 1;
        const currentRoundPicks = (draftPicksData as any[])
          .filter(p => p.round_number === currentRoundNumber)
          .sort((a, b) => a.position_in_round - b.position_in_round);

        // 6. Charger les noms d'utilisateur pour tous les users impliqués
        const allUserIds = [...new Set([
          ...(draftOrderData?.map(item => item.user_id) || []),
          ...currentRoundPicks.map(p => p.user_id)
        ])] as number[];
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('user_id, username')
          .in('user_id', allUserIds);

        if (userError) {
          console.error('Error fetching users:', userError);
        }

        // 7. Construire l'ordre d'affichage depuis les picks de la ronde courante
        // position_in_round - 1 = index dans draft_order (ordonné par draft_position)
        const currentPickerIndexInRound = currentRoundPicks.findIndex(p => p.pick_index === currentPickIdx);

        const teamOrder: DraftTeam[] = currentRoundPicks.map(pick => {
          const currentOwner = userData?.find(u => u.user_id === pick.user_id);
          // Propriétaire original = user à la position de base correspondante
          const baseOwner = draftOrderData?.[pick.position_in_round - 1];
          const isTraded = baseOwner && baseOwner.user_id !== pick.user_id;
          const originalOwner = isTraded ? userData?.find(u => u.user_id === baseOwner?.user_id) : undefined;
          const draftOrderEntry = draftOrderData?.find(o => o.user_id === pick.user_id);
          return {
            id: pick.user_id,
            name: currentOwner?.username || `User ${pick.user_id}`,
            isCurrentPick: pick.pick_index === currentPickIdx,
            isMyTeam: pick.user_id === Number(currentUser),
            timeOfPick: draftOrderEntry?.time_of_pick || null,
            originalOwnerName: isTraded ? (originalOwner?.username || `User ${baseOwner?.user_id}`) : undefined,
            pickIndex: pick.pick_index
          };
        });

        setDraftOrder(teamOrder);
        setCurrentPickIndex(currentPickerIndexInRound);
        setLastRefreshTime(Date.now());

        // 7. Calculer le timer basé sur le pick précédent
        if (currentPickIdx === 0) {
          // Premier pick du draft
          const currentPicker = draftOrderData?.find(o => o.user_id === currentPickerUserId);
          if (currentPicker?.time_of_pick) {
            resetTimerBasedOnPickTime(currentPicker.time_of_pick);
          } else {
            setTimeLeft(pickTimeInSeconds);
            setPrevPickTime(new Date());
          }
        } else {
          // Trouver le picker précédent via draft_picks
          const previousPickRow = draftPicksData.find(p => p.pick_index === currentPickIdx - 1);
          if (previousPickRow) {
            const previousPicker = draftOrderData?.find(o => o.user_id === previousPickRow.user_id);
            if (previousPicker?.time_of_pick) {
              resetTimerBasedOnPickTime(previousPicker.time_of_pick);
            } else {
              setTimeLeft(pickTimeInSeconds);
              setPrevPickTime(new Date());
            }
          }
        }
      } catch (error) {
        console.error('Error in fetchDraftOrder:', error);
      } finally {
        setLoading(false);
      }
    }
    
    // Function to calculate remaining time based on a pick time
    const resetTimerBasedOnPickTime = (pickTimeString: string) => {
      try {
        // Get current server time to compare with pick time
        const now = new Date();
        const pickTime = new Date(pickTimeString);
        setPrevPickTime(pickTime);
        
        // Calculate time elapsed in seconds since the pick was made
        const elapsedSeconds = Math.floor((now.getTime() - pickTime.getTime()) / 1000);
        
        // Calculate remaining time
        const remainingTime = Math.max(0, pickTimeInSeconds - elapsedSeconds);
        
        console.log(`Pick time: ${pickTime}, Elapsed: ${elapsedSeconds}s, Remaining: ${remainingTime}s`);
        
        // Set the timer to the remaining time, or reset if time has expired
        console.log("icitte on set le temp");
        setTimeLeft(remainingTime > 0 ? remainingTime : 0);
      } catch (error) {
        console.error('Error calculating time remaining:', error);
        setTimeLeft(pickTimeInSeconds);
      }
    };
    
    // Initial fetch
    fetchDraftOrder();
    
    // Set up a subscription to track changes to the draft order in real-time
    const subscription = supabase
      .channel('draft-order-changes')
      .on('postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'draft_order',
          filter: `league_id=eq.${selectedLeague.id}`
        }, 
        (payload) => {
          console.log('Draft order update received:', payload);
          fetchDraftOrder(); // Refresh the draft order when changes occur
        }
      )
      .subscribe();
    
  // // Set up an interval to refresh data periodically as a fallback
  // const refreshInterval = setInterval(() => {
  //   const timeSinceLastRefresh = Date.now() - lastRefreshTime;
  //   // Only refresh if it's been more than 20 seconds since the last refresh
  //   if (timeSinceLastRefresh > 20000) {
  //     fetchDraftOrder();
  //   }
  // }, 15000); // Check every 15 seconds for fresher data
     // Set up an interval to refresh data periodically as a fallback
    const refreshInterval = setInterval(() => {
      const timeSinceLastRefresh = Date.now() - lastRefreshTime;
      // Only refresh if it's been more than 2 minutes since the last refresh
      if (timeSinceLastRefresh > 120000) {
        fetchDraftOrder();
      }
    }, 110000); // Check every 1m50 for fresher data (fallback)

    return () => {
      subscription.unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [selectedLeague, currentUser, pickTimeInSeconds, isDraftModeEnabled]);

  // Load autopick status from database
  const loadAutoPick = async () => {
    if (!selectedLeague || !currentUser) return;

    try {
      const { data, error } = await supabase
        .from('leagues_users')
        .select('autopick')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading autopick status:', error);
        return;
      }

      setIsAutoPick(data?.autopick || false);
    } catch (error) {
      console.error('Error loading autopick status:', error);
    }
  };

  // Handle autopick toggle click - show confirmation modal when enabling
  const handleAutoPickToggleClick = () => {
    if (!isAutoPick) {
      // Opening autopick - show confirmation modal
      setShowAutoPickConfirmModal(true);
    } else {
      // Disabling autopick - no confirmation needed
      toggleAutoPick();
    }
  };

  // Confirm autopick activation
  const confirmAutoPickActivation = () => {
    setShowAutoPickConfirmModal(false);
    toggleAutoPick();
  };

  // Toggle autopick status
  const toggleAutoPick = async () => {
    if (!selectedLeague || !currentUser) return;

    try {
      const newAutoPickValue = !isAutoPick;
      
      const { error } = await supabase
        .from('leagues_users')
        .update({ autopick: newAutoPickValue })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser);

      if (error) {
        console.error('Error updating autopick status:', error);
        toast.error('Erreur lors de la mise à jour du repêchage automatique');
        return;
      }

      setIsAutoPick(newAutoPickValue);
      toast.success(newAutoPickValue ? 'Repêchage automatique activé' : 'Repêchage automatique désactivé');
    } catch (error) {
      console.error('Error toggling autopick:', error);
      toast.error('Erreur lors de la mise à jour du repêchage automatique');
    }
  };

  // Load priority list
  const loadPriorityList = async () => {
    if (!selectedLeague || !currentUser) return;

    try {
      // 1. Get player IDs and positions from list_prioritaire
      const { data: priorityData, error: priorityError } = await supabase
        .from('list_prioritaire')
        .select('player_id, position, player_position, isnhl, reason_notpick, salary')
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .order('position', { ascending: true });

      if (priorityError) throw priorityError;

      if (!priorityData || priorityData.length === 0) {
        setPriorityList([]);
        return;
      }

      // 2. Get player details from the free agent data
      const playerIds = priorityData.map(item => item.player_id);

      // First check skaters
      const { data: playerData, error: playerError } = await supabase
        .rpc('get_free_agents_by_league', { p_league_id: selectedLeague.id });

      if (playerError) throw playerError;

      // Then check goalies
      const { data: goalieData, error: goalieError } = await supabase
        .rpc('get_free_agents_by_league_goalie', { p_league_id: selectedLeague.id });

      if (goalieError) throw goalieError;

      // 3. For players who are not free agents (already drafted), get their data from player_stats and show_player_onlycurrentday
      const freeAgentIds = [
        ...(playerData || []).map(p => p.playerid),
        ...(goalieData || []).map(g => g.playerid)
      ];
      
      // Find player IDs that are in priority list but not in free agents (drafted players)
      const draftedPlayerIds = playerIds.filter(id => !freeAgentIds.includes(id));
      
      let draftedPlayerData: any[] = [];
      if (draftedPlayerIds.length > 0) {
        // Get data for drafted skaters from show_player_onlycurrentday
        const { data: draftedSkaters, error: skaterError } = await supabase
          .from('show_player_onlycurrentday')
          .select('playerid, skaterfullname, positioncode, teamabbrevs, goals, assists')
          .in('playerid', draftedPlayerIds);
          
        if (!skaterError && draftedSkaters && draftedSkaters.length > 0) {
          draftedPlayerData = [...draftedPlayerData, ...draftedSkaters];
        } else {
          // Fallback to player_stats if show_player_onlycurrentday doesn't have data
          const { data: fallbackSkaters, error: fallbackError } = await supabase
            .from('player_stats')
            .select('playerid, skaterfullname, positioncode, teamabbrevs, goals, assists')
            .in('playerid', draftedPlayerIds);
            
          if (!fallbackError && fallbackSkaters) {
            draftedPlayerData = [...draftedPlayerData, ...fallbackSkaters];
          }
        }
        
        // Get data for drafted goalies
        const { data: draftedGoalies, error: goalieDataError } = await supabase
          .from('goalie_stats')
          .select('playerid, goaliefullname as skaterfullname, teamabbrevs, wins, savepct')  
          .in('playerid', draftedPlayerIds);
          
        if (!goalieDataError && draftedGoalies) {
          // Add "G" position for goalies and transform to match skater structure
          const formattedGoalies = draftedGoalies.map(g => ({
            ...g,
            positioncode: 'G',
            goals: 0,
            assists: 0
          }));
          
          draftedPlayerData = [...draftedPlayerData, ...formattedGoalies];
        }
      }

      // Combine and filter by playerIds
      const allPlayers = [
        ...(playerData || []).map(p => ({
          id: p.playerid,
          name: p.skaterfullname,
          position: p.positioncode,
          teamabbrevs: p.teamabbrevs,
          goals: p.goals,
          assists: p.assists,
          points: p.points,
          salary: p.salary,
          isGoalie: false,
          isDrafted: false
        })),
        ...(goalieData || []).map(g => ({
          id: g.playerid,
          name: g.goaliefullname,
          position: 'G',
          teamabbrevs: g.teamabbrevs,
          goals: g.goals,
          assists: g.assists,
          points: g.goals + g.assists,
          salary: g.salary,
          wins: g.wins,
          savepct: g.savepct,
          isGoalie: true,
          isDrafted: false
        })),
        // Add the drafted players to the list with data from player_stats/goalie_stats
        ...(draftedPlayerData || []).map(p => ({
          id: p.playerid,
          name: p.skaterfullname,
          position: p.positioncode,
          teamabbrevs: p.teamabbrevs,
          goals: p.goals,
          assists: p.assists,
          points: (p.goals || 0) + (p.assists || 0),
          salary: priorityData.find(item => item.player_id === p.playerid)?.salary || 0,
          wins: p.wins,
          savepct: p.savepct,
          isGoalie: p.positioncode === 'G',
          isDrafted: true
        }))
      ];

      // Create a position map from priorityData
      const positionMap = new Map();
      const isNhlMap = new Map();
      const reasonMap = new Map();
      priorityData.forEach(item => {
        positionMap.set(item.player_id, item.position);
        isNhlMap.set(item.player_id, item.isnhl !== undefined ? item.isnhl : true); // Default to true if undefined
        reasonMap.set(item.player_id, item.reason_notpick);
      });

      // Map the priority list with all player data from the database
      // We map directly from priorityData to ensure we include all players in the priority list
      const mappedPlayers = priorityData
        .map(item => {
          // Find this player in our allPlayers array if available
          const player = allPlayers.find(p => p.id === item.player_id);
          
          // If we found the player, use that data
          if (player) {
            return {
              ...player,
              isnhl: isNhlMap.get(item.player_id), // Add the isnhl property from our map
              reason_notpick: reasonMap.get(item.player_id) // Add the reason_notpick property from our map
            };
          } else {
            // If the player is not found at all, create a basic entry with what we know
            return {
              id: item.player_id,
              name: `Player ID: ${item.player_id}`,
              position: item.player_position || 'Unknown',
              teamabbrevs: 'NHL',
              salary: item.salary || 0,
              isnhl: isNhlMap.get(item.player_id),
              reason_notpick: reasonMap.get(item.player_id),
              isDrafted: true
            };
          }
        });

      // Sort the mapped players by position from the database
      const sortedPlayers = mappedPlayers.sort((a, b) => {
        const posA = positionMap.get(a.id) || 0;
        const posB = positionMap.get(b.id) || 0;
        return posA - posB;
      }) as PriorityListPlayer[];

      setPriorityList(sortedPlayers);
      setLastPriorityListRefresh(Date.now());
    } catch (error) {
      console.error('Error loading priority list:', error);
    }
  };

  // Refresh priority list periodically
  useEffect(() => {
    if (!selectedLeague || !currentUser) return;

    // Initial load
    loadPriorityList();
    loadAutoPick(); // Load autopick status
    
    // // Set up an interval to refresh the priority list
    // const interval = setInterval(() => {
    //   const timeSinceLastRefresh = Date.now() - lastPriorityListRefresh;
    //   // Refresh when it's been more than 2 minutes since last refresh
    //   if (timeSinceLastRefresh > 120000) {
    //     loadPriorityList();
    //   }
    // }, 110000); // Check every 1m50

    // Set up an interval to refresh the priority list
    const interval = setInterval(() => {
      const timeSinceLastRefresh = Date.now() - lastPriorityListRefresh;
      // Refresh when it's been more than 2 minutes since last refresh
      if (timeSinceLastRefresh > 20000) {
        loadPriorityList();
      }
    }, 15000); // Check every 1m50
    
    return () => clearInterval(interval);
  }, [selectedLeague, currentUser]);

  const loadAllRoundsOrder = async () => {
    if (!selectedLeague) return;
    setLoadingAllRounds(true);
    try {
      const [{ data: allPicks }, { data: draftOrderData }] = await Promise.all([
        supabase
          .from('draft_picks')
          .select('pick_index, round_number, position_in_round, user_id')
          .eq('league_id', selectedLeague.id)
          .order('pick_index', { ascending: true }),
        supabase
          .from('draft_order')
          .select('user_id, draft_position')
          .eq('league_id', selectedLeague.id)
          .order('draft_position', { ascending: true }),
      ]);

      if (!allPicks?.length) { setAllRoundsData([]); return; }

      const allUserIds = [...new Set([
        ...(draftOrderData?.map(o => o.user_id) || []),
        ...allPicks.map(p => p.user_id)
      ])] as number[];
      const { data: userData } = await supabase
        .from('users')
        .select('user_id, username')
        .in('user_id', allUserIds);

      const rounds: Record<number, Array<{userId: number, name: string, originalOwnerName?: string, pickIndex: number}>> = {};
      for (const pick of allPicks) {
        if (!rounds[pick.round_number]) rounds[pick.round_number] = [];
        const currentOwner = userData?.find(u => u.user_id === pick.user_id);
        const baseOwner = draftOrderData?.[pick.position_in_round - 1];
        const isTraded = baseOwner && baseOwner.user_id !== pick.user_id;
        const originalOwner = isTraded ? userData?.find(u => u.user_id === baseOwner?.user_id) : undefined;
        rounds[pick.round_number].push({
          userId: pick.user_id,
          name: currentOwner?.username || `User ${pick.user_id}`,
          originalOwnerName: isTraded ? (originalOwner?.username || `User ${baseOwner?.user_id}`) : undefined,
          pickIndex: pick.pick_index
        });
      }

      const result = Object.entries(rounds)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([round, picks]) => ({ round: Number(round), picks }));
      setAllRoundsData(result);
    } catch (e) {
      console.error('Error loading all rounds order:', e);
    } finally {
      setLoadingAllRounds(false);
    }
  };

  // Calculate picks until my turn using draft_picks
  const currentTeam = draftOrder[currentPickIndex];
  const isMyTurn = currentTeam?.isMyTeam === true;
  
  const picksUntilMyTurn = (() => {
    if (isMyTurn) return 0;
    if (!currentUser) return 0;

    // Trouver le prochain pick de l'utilisateur dans nextPicksPreview
    const myNextPick = nextPicksPreview.find(p => p.user_id === Number(currentUser));
    
    if (!myNextPick) return 0; // Pas de prochain pick trouvé dans les 15 prochains
    
    return myNextPick.pick_index - currentPick;
  })();

  const moveToNextPick = async () => {
    console.log("rentre dans ma function movetonextpick");
    if (isMovingToNextPick.current || draftOrder.length === 0 || !selectedLeague) return;
    isMovingToNextPick.current = true;

    try {
      // 1) Read current_pick from league_settings
      const { data: lsData, error: lsErr } = await supabase
        .from('league_settings')
        .select('current_pick')
        .eq('league_id', selectedLeague.id)
        .single();
      if (lsErr) throw lsErr;

      const currentPickIdx = lsData?.current_pick ?? 0; // number of picks already completed
      const nextPickIdx = currentPickIdx + 1;

      // 2) Find current and next pick users from draft_picks
      const [{ data: curPickRow, error: curErr }, { data: nextPickRow, error: nextErr }] = await Promise.all([
        supabase
          .from('draft_picks')
          .select('user_id')
          .eq('league_id', selectedLeague.id)
          .eq('pick_index', currentPickIdx)
          .maybeSingle(),
        supabase
          .from('draft_picks')
          .select('user_id')
          .eq('league_id', selectedLeague.id)
          .eq('pick_index', nextPickIdx)
          .maybeSingle(),
      ]);
      if (curErr) throw curErr;
      if (nextErr) throw nextErr;

      const currentPickUserId = curPickRow?.user_id as number | undefined;
      const nextPickUserId = nextPickRow?.user_id as number | undefined;

      // If there's no next pick, we might be at the end of the draft
      if (!nextPickUserId) {
        // Still stamp the current picker time and clear myturn
        const montrealTime = getMontrealTime();
        if (currentPickUserId) {
          await supabase
            .from('draft_order')
            .update({ myturn: false, time_of_pick: montrealTime })
            .eq('league_id', selectedLeague.id)
            .eq('user_id', currentPickUserId);
        }

        // Update current_pick to reflect the last pick was made
        await supabase
          .from('league_settings')
          .update({ current_pick: nextPickIdx })
          .eq('league_id', selectedLeague.id);

        // Check if this completes the draft and disable it if needed
        await checkAndFinishDraft();

        // Local state update: mark all as not current
        setDraftOrder(prev => prev.map(t => ({ ...t, isCurrentPick: false })));
        isMovingToNextPick.current = false;
        return;
      }

      // 3) Write changes: set time for current, set myturn for next, increment current_pick
      const montrealTime = getMontrealTime();
      if (currentPickUserId) {
        await supabase
          .from('draft_order')
          .update({ myturn: false, time_of_pick: montrealTime })
          .eq('league_id', selectedLeague.id)
          .eq('user_id', currentPickUserId);
      }

      await supabase
        .from('draft_order')
        .update({ myturn: true })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', nextPickUserId);

      await supabase
        .from('league_settings')
        .update({ current_pick: nextPickIdx })
        .eq('league_id', selectedLeague.id);

      // Non-blocking email notifications (current + upcoming pickers)
      try {
        // Email au picker actif
        const { data: nextUser } = await supabase
          .from('users')
          .select('email, username')
          .eq('user_id', nextPickUserId)
          .maybeSingle();
        if (nextUser?.email) {
          await sendDraftTurnEmail({
            userEmail: nextUser.email,
            username: nextUser.username || '',
            leagueName: selectedLeague.name,
            pickNumber: nextPickIdx + 1,
            type: 'current',
          });
        }
        // Email au picker d'après (look-ahead)
        const { data: upcomingPickRow } = await supabase
          .from('draft_picks')
          .select('user_id')
          .eq('league_id', selectedLeague.id)
          .eq('pick_index', nextPickIdx + 1)
          .maybeSingle();
        if (upcomingPickRow?.user_id) {
          const { data: upcomingUser } = await supabase
            .from('users')
            .select('email, username')
            .eq('user_id', upcomingPickRow.user_id)
            .maybeSingle();
          if (upcomingUser?.email) {
            await sendDraftTurnEmail({
              userEmail: upcomingUser.email,
              username: upcomingUser.username || '',
              leagueName: selectedLeague.name,
              pickNumber: nextPickIdx + 2,
              type: 'upcoming',
            });
          }
        }
      } catch (emailErr) {
        console.error('Draft turn email failed (non-blocking):', emailErr);
      }

      setCurrentPick(nextPickIdx);

      // Check if this completes the draft after updating the pick
      await checkAndFinishDraft();

      // 4) Local state sync
      setDraftOrder(prevOrder => {
        const montrealTimeISO = montrealTime;
        return prevOrder.map(team => {
          if (team.id === currentPickUserId) {
            return { ...team, isCurrentPick: false, timeOfPick: montrealTimeISO };
          }
          if (team.id === nextPickUserId) {
            return { ...team, isCurrentPick: true };
          }
          return team;
        });
      });

      // Move currentPickIndex to the next user's position in local draftOrder
      const nextIndexLocal = draftOrder.findIndex(t => t.id === nextPickUserId);
      if (nextIndexLocal !== -1) setCurrentPickIndex(nextIndexLocal);

      // Reset timer to full time
      setTimeLeft(pickTimeInSeconds);
      setPrevPickTime(new Date());

    } catch (error) {
      console.error('Error updating draft order status (draft_picks-based):', error);
      toast.error('Erreur lors du passage au prochain choix');
    } finally {
      isMovingToNextPick.current = false;
    }
  };

  // Client-side timer that syncs with the server state
  useEffect(() => {
    // Clear any existing timer when component mounts or currentPickIndex changes
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
    }
    
    // Set up a new timer
    timerRef.current = window.setInterval(() => {
      setTimeLeft(prevTime => {
        // Check if we have a valid previous pick time to calculate from
        if (prevPickTime) {
          const now = new Date();
          const elapsedSeconds = Math.floor((now.getTime() - prevPickTime.getTime()) / 1000);
          const calculatedTimeLeft = Math.max(0, pickTimeInSeconds - elapsedSeconds);
          
          // Only update if significantly different (to avoid multiple sources of truth conflicts)
          if (Math.abs(calculatedTimeLeft - prevTime) > 2) {
            return calculatedTimeLeft;
          }
          
          // Normal countdown
          return prevTime > 0 ? prevTime - 1 : 0;
        }
        
        // Fallback to normal countdown if no prevPickTime
        if (prevTime <= 1) {
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Clean up the timer when component unmounts or currentPickIndex changes
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentPickIndex, pickTimeInSeconds, prevPickTime]);

  // Format timer display as HH:MM:SS
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format time display for pick history
  const formatPickTime = (timeString: string | null | undefined) => {
    if (!timeString) return 'Pas encore choisi';
    
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const handleMakePick = async () => {
    if (!isMyTurn || !selectedLeague) return;
    
    try {
      // Record the time of pick using Montreal timezone
      const montrealTime = getMontrealTime();
      
      // Update the database with the pick time
      await supabase
        .from('draft_order')
        .update({ 
          time_of_pick: montrealTime 
        })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser);
        
      // Update local state
      setDraftOrder(prevOrder => {
        return prevOrder.map(team => {
          if (team.id === Number(currentUser)) {
            return { ...team, timeOfPick: montrealTime };
          }
          return team;
        });
      });
      
      // Navigate to free agents screen for picking
      navigate('/free-agents', { 
        state: { 
          fromDraft: true,
          draftPickNumber: currentPickIndex + 1 
        },
        replace: true
      });
    } catch (error) {
      console.error('Error recording draft pick time:', error);
      
      // If there's an error recording the time, still allow the user to make their pick
      navigate('/free-agents', { 
        state: { 
          fromDraft: true,
          draftPickNumber: currentPickIndex + 1 
        },
        replace: true
      });
    }
  };

  const handleAddToPriorityList = () => {
    navigate('/free-agents', { 
      state: { 
        fromDraft: true,
        priorityMode: true 
      }
    });
  };

  const handleRemoveFromPriorityList = async (playerId: number) => {
    if (!selectedLeague || !currentUser) return;

    try {
      const { error } = await supabase
        .from('list_prioritaire')
        .delete()
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('player_id', playerId);

      if (error) throw error;

      // Update the UI
      setPriorityList(prev => prev.filter(player => player.id !== playerId));
      toast.success('Joueur retiré de la liste prioritaire');
      
      // Refresh the priority list after removing a player
      setTimeout(() => {
        loadPriorityList();
      }, 500);
    } catch (error) {
      console.error('Error removing player from priority list:', error);
      toast.error('Erreur lors du retrait du joueur');
    }
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getPlayerImageUrl = (player: PriorityListPlayer) => {
    if (!player.teamabbrevs || imageErrors[player.id]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.id}.png`;
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  // Handle drag start
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedPlayer(index);
    e.dataTransfer.effectAllowed = 'move';
    // Snapshot original order to detect changes on drop
    dragOriginalOrderRef.current = priorityList.map(p => p.id);
    // This makes the dragged item visually dragged
    if (e.currentTarget instanceof HTMLElement) {
      setTimeout(() => {
        if (e.currentTarget instanceof HTMLElement) {
          e.currentTarget.style.opacity = '0.4';
        }
      }, 0);
    }
  };

  // Handle drag end
  const handleDragEnd = async (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedPlayer(null);
    // If order changed during drag, auto-save new order silently
    try {
      const original = dragOriginalOrderRef.current;
      dragOriginalOrderRef.current = null;
      const current = priorityList.map(p => p.id);
      const changed = !original || original.length !== current.length || original.some((id, i) => id !== current[i]);
      if (changed && priorityList.length > 0) {
        await savePriorityListOrder(priorityList, { silent: true });
      }
    } catch (err) {
      console.error('Error auto-saving on drag end:', err);
    }
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedPlayer === null || draggedPlayer === index) return;
    
    // Reorder the list
    const updatedList = [...priorityList];
    const draggedItem = updatedList[draggedPlayer];
    
    // Remove item at draggedIndex
    updatedList.splice(draggedPlayer, 1);
    
    // Insert at new position
    updatedList.splice(index, 0, draggedItem);
    
    // Update state and draggedIndex
    setPriorityList(updatedList);
    setDraggedPlayer(index);
  };

  // Save reordered list to database (supports silent autosave and passing a specific list)
  const savePriorityListOrder = async (
    listOverride?: PriorityListPlayer[],
    options?: { silent?: boolean }
  ) => {
    const listToSave = listOverride ?? priorityList;
    if (!selectedLeague || !currentUser || listToSave.length === 0) return;

    try {
      // 1. Delete all current entries for this user in this league
      const { error: deleteError } = await supabase
        .from('list_prioritaire')
        .delete()
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser);

      if (deleteError) throw deleteError;

      // 2. Insert items in the new order with position field and isnhl status
      const newEntries = listToSave.map((player, index) => ({
        league_id: selectedLeague.id,
        user_id: Number(currentUser),
        player_id: player.id,
        position: index + 1,
        salary: player.salary,
        player_position: player.position,
        isnhl: player.isnhl !== undefined ? player.isnhl : true,
        reason_notpick: player.reason_notpick,
      }));

      const { error: insertError } = await supabase
        .from('list_prioritaire')
        .insert(newEntries);

      if (insertError) throw insertError;

      if (!options?.silent) {
        toast.success('Ordre de priorité sauvegardé');
      }

      // Refresh the list after saving to ensure it's up to date
      setTimeout(() => {
        loadPriorityList();
      }, 500);
  } catch (error) {
      console.error('Error saving priority list order:', error);
      toast.error('Erreur lors de la sauvegarde de l\'ordre de priorité');
  }
  };

  // Move player up in priority list
  const movePlayerUp = async (index: number) => {
    if (index === 0) return; // Already at top
    
    const newList = [...priorityList];
    const temp = newList[index];
    newList[index] = newList[index - 1];
    newList[index - 1] = temp;
    
    setPriorityList(newList);
    // Auto-save silently
    await savePriorityListOrder(newList, { silent: true });
  };
  
  // Move player down in priority list
  const movePlayerDown = async (index: number) => {
    if (index === priorityList.length - 1) return; // Already at bottom
    
    const newList = [...priorityList];
    const temp = newList[index];
    newList[index] = newList[index + 1];
    newList[index + 1] = temp;
    
    setPriorityList(newList);
    // Auto-save silently
    await savePriorityListOrder(newList, { silent: true });
  };

  // Toggle NHL/AHL status for a player in the priority list
  const togglePlayerTeam = async (index: number) => {
    const updatedList = [...priorityList];
    updatedList[index] = {
      ...updatedList[index],
      isnhl: !updatedList[index].isnhl,
    };
    setPriorityList(updatedList);
    // Auto-save silently
    await savePriorityListOrder(updatedList, { silent: true });
  };

  // Clear the reason for not picking a player
  const clearPlayerReason = async (playerId: number) => {
    if (!selectedLeague || !currentUser) return;
    
    try {
      const { error } = await supabase
        .from('list_prioritaire')
        .update({ reason_notpick: null })
        .eq('league_id', selectedLeague.id)
        .eq('user_id', currentUser)
        .eq('player_id', playerId);
        
      if (error) throw error;
      
      // Update the UI
      setPriorityList(prev => prev.map(player => {
        if (player.id === playerId) {
          return {
            ...player,
            reason_notpick: null
          };
        }
        return player;
      }));
      
      toast.success('Raison effacée');
    } catch (error) {
      console.error('Error clearing reason:', error);
      toast.error('Erreur lors de l\'effacement de la raison');
    }
  };

  if (loading) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  // If draft mode is not enabled, show a message and don't render the draft page
  if (isDraftModeEnabled === false) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <AlertCircle className="h-12 w-12 text-warning mb-3" />
          <h2 className="card-title text-center mb-3">
            {isDraftCompleted 
              ? (language === 'fr' ? 'Repêchage Terminé' : 'Draft Completed')
              : (language === 'fr' ? 'Mode Repêchage Non Activé' : 'Draft Mode Not Enabled')
            }
          </h2>
          <p className="mb-4">
            {isDraftCompleted 
              ? (language === 'fr' 
                  ? 'Le repêchage est maintenant terminé ! Tous les choix ont été effectués avec succès.'
                  : 'The draft is now completed! All picks have been made successfully.')
              : (language === 'fr' 
                  ? 'Le commissaire de la ligue doit activer le mode repêchage dans les paramètres de la ligue pour accéder à cette fonction.'
                  : 'The league commissioner must enable draft mode in the league settings to access this function.')
            }
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/my-office')}
          >
            {language === 'fr' ? 'Retour au Bureau' : 'Return to Office'}
          </button>
        </div>
      </div>
    );
  }

  // Mode guard
  if (!modeLoading && isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="dynasty-only"
        title="Repêchage — Mode Dynasty / Keeper uniquement"
        message="La draft n'est pas disponible en mode Standard Flexible. Ce mode utilise la construction libre d'équipe via « Bâtir mon équipe ». La draft est une fonctionnalité exclusive au mode Dynasty/Keeper."
        actionLabel="Bâtir mon équipe"
        actionPath="/build-my-team"
      />
    );
  }

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => navigate('/my-office')}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Users className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">Repêchage</h2>
          <p className="text-muted">{selectedLeague?.name}</p>
          
          {/* Info badge for draft progress */}
          <div className="d-inline-flex align-items-center mt-2">
            <span className="badge bg-info d-flex align-items-center">
              <Info size={14} className="me-1" />
              {totalDraftPicks > 0 
                ? `Pick ${currentPick + 1} sur ${totalDraftPicks}` 
                : 'Chargement...'}
            </span>

          </div>
        </div>
        <div className="row g-4">
          {/* Draft Order Column */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-primary text-white d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <List size={20} className="me-2" />
                  <h5 className="mb-0">Ordre de Sélection</h5>
                </div>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => { loadAllRoundsOrder(); setShowDraftOrderModal(true); }}
                >
                  Voir toutes les rondes
                </button>
              </div>
              <div className="card-body">
                {isAutoPick && (
                  <div className="mb-3">
                    <div className="alert alert-warning d-flex align-items-center">
                      <AlertTriangle size={16} className="me-2" />
                      <span>
                        <strong>Repêchage automatique activé :</strong> Le système prendra le prochain joueur disponible dans votre liste. Si aucun joueur de votre liste n'est disponible, le système attendra votre sélection manuelle.
                      </span>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <div className="alert alert-info d-flex align-items-center">
                    <Clock size={16} className="me-2" />
                    <span>
                      Temps par sélection (Il peut y avoir jusqu'à 2 minutes de délai une fois le chrono écoulé avant de passer au suivant): {pickTimeInMinutes} minutes
                    </span>
                  </div>
                </div>
                
                <div className="list-group">
                  {draftOrder.map((team) => (
                    <div
                      key={team.pickIndex ?? team.id}
                      className={`list-group-item ${
                        team.isCurrentPick ? 'active' : ''
                      } ${team.isMyTeam && !team.isCurrentPick ? 'border-primary' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div>
                          <span>{team.name}</span>
                          {team.originalOwnerName && (
                            <div>
                              <small className={team.isCurrentPick ? 'text-warning' : 'text-muted'}>
                                via {team.originalOwnerName}
                              </small>
                            </div>
                          )}
                        </div>
                        {team.isCurrentPick && (
                          <span className={`badge ${timeLeft <= 30 ? 'bg-danger' : 'bg-primary'}`}>
                            {formatTime(timeLeft)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status Bar */}
          <div className="col-md-12">
            <div className="alert alert-info mt-4 d-flex align-items-center">
              <AlertCircle size={20} className="me-2" />
              {isMyTurn ? (
                <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                  <span>C'est ton tour!</span>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      className="btn btn-success"
                      onClick={handleMakePick}
                    >
                      Faire ma sélection
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={moveToNextPick}
                    >
                      J'ai fini mon équipe
                    </button>
                  </div>
                </div>
              ) : picksUntilMyTurn > 0 ? (
                <span>Ce n'est pas ton tour pour le moment. Lorsque ce sera ton tour, tu pourras sélectionner un joueur.</span>
              ) : (
                <span>En attente du prochain tour</span>
              )}
            </div>
          </div>

          {/* Recent Picks Column */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-primary text-white d-flex align-items-center">
                <Clock size={20} className="me-2" />
                <h5 className="mb-0">Dernières Sélections</h5>
              </div>
              <div className="card-body p-2" style={{ maxHeight: 500, overflowY: 'auto' }}>
                {recentDraftPicks.length === 0 && (
                  <div className="text-center text-muted small py-3">
                    Aucun choix pour le moment
                  </div>
                )}
                <ul className="list-unstyled mb-0">
                  {recentDraftPicks.map((pick, idx) => {
                    const isFirst = idx === 0;
                    return (
                      <li
                        key={pick.id}
                        className={`mb-2 rounded border position-relative ${isFirst ? 'border-success shadow-sm' : 'border-secondary'} bg-dark text-white px-2 py-2`}
                        style={{ fontSize: '.9rem' }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className={`badge ${isFirst ? 'bg-success' : 'bg-secondary'} text-uppercase`} style={{ letterSpacing: '.5px' }}>
                                {pick.pick}
                              </span>
                              <strong className="text-truncate" style={{ maxWidth: 140 }}>{pick.player}</strong>
                            </div>
                            <div className="mt-1 d-flex align-items-center flex-wrap gap-2">
                              <span className="badge bg-primary" style={{ fontWeight: 500 }}>{pick.team}</span>
                              {pick.timeOfPick && (
                                <span className="text-muted d-inline-flex align-items-center small">
                                  <Clock size={12} className="me-1 opacity-75" />
                                  {formatPickTime(pick.timeOfPick)}
                                </span>
                              )}
                            </div>
                          </div>
                          {isFirst && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: '.65rem' }}>Plus récent</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* Priority List Column */}
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-primary text-white d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Star size={20} className="me-2" />
                  <h5 className="mb-0">Liste de sélection automatique</h5>
                </div>
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="autoPickToggle"
                    checked={isAutoPick}
                    onChange={handleAutoPickToggleClick}
                    style={{ fontSize: '1.1em' }}
                  />
                  <label className="form-check-label text-white ms-2" htmlFor="autoPickToggle">
                    Repêchage auto
                  </label>
                </div>
              </div>
              <div className="card-body">
                {priorityList.length === 0 ? (
                  <p className="text-center text-muted mb-3">
                    Votre liste prioritaire est vide. Ajoutez des joueurs pour une sélection automatique si votre temps est écoulé.
                  </p>
                ) : (
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <Info size={16} className="me-2 text-primary" />
                      <small className="text-muted">
                        Glissez-déposez les joueurs ou utilisez les flèches pour modifier l'ordre
                      </small>
                    </div>
                    
                    <div className="list-group mb-3">
                      {priorityList.map((player, index) => (
                        <div 
                          key={player.id}
                          className="list-group-item d-flex align-items-stretch"
                          draggable={true}
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, index)}
                          style={{ 
                            cursor: 'grab',
                            opacity: draggedPlayer === index ? 0.5 : 1,
                            minHeight: '64px',
                            paddingRight: '2.5rem', // espace pour le bouton X
                            position: 'relative'
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex flex-grow-1">
                              <div className="me-1 d-flex flex-column align-items-center justify-content-center" style={{ minWidth: 32 }}>
                                <GripVertical size={13} className="text-muted mb-1" />
                                <div className="btn-group-vertical" style={{ gap: '0.18rem' }}>
                                  <button
                                    className="btn btn-outline-secondary px-0 py-0"
                                    style={{ minWidth: 16, minHeight: 16, fontSize: '0.7rem', borderRadius: '0.25rem', marginBottom: '0.08rem', touchAction: 'manipulation' }}
                                    disabled={index === 0}
                                    onClick={() => movePlayerUp(index)}
                                    aria-label="Monter le joueur"
                                  >
                                    <ArrowUp size={9} />
                                  </button>
                                  <button
                                    className="btn btn-outline-secondary px-0 py-0"
                                    style={{ minWidth: 16, minHeight: 16, fontSize: '0.7rem', borderRadius: '0.25rem', touchAction: 'manipulation' }}
                                    disabled={index === priorityList.length - 1}
                                    onClick={() => movePlayerDown(index)}
                                    aria-label="Descendre le joueur"
                                  >
                                    <ArrowDown size={9} />
                                  </button>
                                </div>
                              </div>
                              <div className="d-flex flex-column flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  <img 
                                    src={getPlayerImageUrl(player)} 
                                    alt={player.name}
                                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', marginRight: '0.5rem', border: '2px solid #fff' }}
                                    className="shadow-sm"
                                    onError={() => handleImageError(player.id)}
                                  />
                                  <div className="flex-grow-1 d-flex flex-column justify-content-center" style={{ minWidth: 0 }}>
                                    <h5 className="mb-1 text-truncate" style={{ fontSize: '1rem', fontWeight: 700, lineHeight: '1.2', wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
                                      {player.name}
                                    </h5>
                                    <div className="d-flex gap-1 align-items-center mb-1 flex-wrap" style={{ flexWrap: 'wrap' }}>
                                      <small className="text-muted">{player.position}</small>
                                      <span 
                                        className={`badge ${player.isnhl ? 'bg-primary' : 'bg-secondary'} small`}
                                        style={{ cursor: 'pointer', fontSize: '0.85rem', padding: '0.22em 0.45em', minWidth: 32 }}
                                        onClick={() => togglePlayerTeam(index)}
                                      >
                                        {player.isnhl !== false ? 'NHL' : 'AHL'}
                                      </span>
                                      {index === 0 && (
                                        <span className="badge bg-warning text-dark small" style={{ fontSize: '0.85rem', padding: '0.22em 0.45em', minWidth: 70 }}>Prochain choix auto</span>
                                      )}
                                      {player.isDrafted && (
                                        <span className="badge bg-danger text-white small" style={{ fontSize: '0.85rem', padding: '0.22em 0.45em', minWidth: 70 }}>Repêché par un autre équipe</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {player.reason_notpick && (
                                  <div className="alert alert-warning py-1 px-2 mb-1 d-flex align-items-center" style={{ fontSize: '0.95rem' }}>
                                    <AlertTriangle size={18} className="me-2" />
                                    <div className="flex-grow-1">{player.reason_notpick}</div>
                                    <button 
                                      className="btn btn-sm btn-close" 
                                      style={{ fontSize: '0.8rem', padding: '0.2rem' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        clearPlayerReason(player.id);
                                      }}
                                      aria-label="Close"
                                    ></button>
                                  </div>
                                )}
                                <div className="d-flex gap-3 small mb-1 flex-wrap">
                                  {player.isGoalie ? (
                                    <>
                                      <span>V: <b>{player.wins || 0}</b></span>
                                      <span>%ARR: <b>{player.savepct?.toFixed(3) || '0.000'}</b></span>
                                    </>
                                  ) : (
                                    <>
                                      <span>B: <b>{player.goals || 0}</b></span>
                                      <span>A: <b>{player.assists || 0}</b></span>
                                      <span>P: <b>{player.points || 0}</b></span>
                                    </>
                                  )}
                                </div>
                                <small className="text-muted d-block" style={{ fontSize: '1rem', fontWeight: 600 }}>
                                  {formatCurrency(player.salary)}
                                </small>
                              </div>
                            </div>
                            <button 
                              className="btn btn-sm btn-outline-danger align-self-center position-absolute end-0 top-50 translate-middle-y me-2"
                              style={{ zIndex: 2 }}
                              onClick={() => handleRemoveFromPriorityList(player.id)}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Enregistrement automatique activé: le bouton de sauvegarde manuelle a été supprimé */}
                  </>
                )}
                
                <button 
                  className="btn btn-primary w-100"
                  onClick={handleAddToPriorityList}
                >
                  <Plus size={18} className="me-2" />
                  Ajouter un joueur
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal ordre de draft — toutes les rondes */}
      {showDraftOrderModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title d-flex align-items-center">
                  <List size={20} className="me-2" />
                  Ordre de repêchage — toutes les rondes
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDraftOrderModal(false)} />
              </div>
              <div className="modal-body">
                {loadingAllRounds ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="mt-2 text-muted">Chargement...</p>
                  </div>
                ) : allRoundsData.length === 0 ? (
                  <div className="text-center p-4 text-muted">
                    Aucune donnée disponible. Assurez-vous que l'ordre de draft a été généré.
                  </div>
                ) : (
                  allRoundsData.map(({ round, picks }) => (
                    <div key={round} className="mb-4">
                      <h6 className="text-primary border-bottom pb-2 mb-2 fw-bold">Ronde {round}</h6>
                      <div className="list-group">
                        {picks.map((pick, idx) => (
                          <div
                            key={pick.pickIndex}
                            className={`list-group-item py-2 ${pick.userId === Number(currentUser) ? 'border-primary' : ''}`}
                          >
                            <div className="d-flex align-items-center">
                              <span className="badge bg-secondary me-2" style={{ minWidth: '2.2rem', textAlign: 'center' }}>
                                {idx + 1}
                              </span>
                              <div>
                                <span className={pick.userId === Number(currentUser) ? 'fw-bold text-primary' : ''}>
                                  {pick.name}
                                </span>
                                {pick.originalOwnerName && (
                                  <div>
                                    <small className="text-warning fst-italic">
                                      via {pick.originalOwnerName}
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDraftOrderModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation pour l'activation du repêchage automatique */}
      {showAutoPickConfirmModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-white text-dark" ref={autoPickModalRef}>
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title d-flex align-items-center text-dark">
                  <AlertTriangle size={20} className="me-2" />
                  Attention - Repêchage automatique
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAutoPickConfirmModal(false)}
                />
              </div>
              <div className="modal-body text-dark">
                <p className="mb-3">
                  <strong>Êtes-vous sûr de vouloir activer le repêchage automatique ?</strong>
                </p>
                <div className="alert bg-light border border-warning text-dark d-flex align-items-start">
                  <AlertCircle size={20} className="me-2 flex-shrink-0 mt-1 text-warning" />
                  <div className="text-dark">
                    <ul className="mb-0 mt-1 text-dark">
                      <li>Le système sélectionnera le <strong>prochain joueur disponible</strong> dans votre liste de sélection automatique.</li>
                      <li>Si <strong>aucun joueur</strong> de votre liste n'est disponible, le système attendra votre <strong>choix manuel</strong>.</li>
                    </ul>
                  </div>
                </div>
                <p className="text-secondary small mb-0">
                  Assurez-vous d'avoir des joueurs dans votre liste prioritaire pour profiter de cette option.
                </p>
              </div>
              <div className="modal-footer text-dark">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAutoPickConfirmModal(false)}
                >
                  Annuler
                </button>
                <button 
                  type="button" 
                  className="btn btn-warning"
                  onClick={confirmAutoPickActivation}
                >
                  Je comprends, activer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}